import React, { Component, createRef } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { get as _get } from "lodash";
import Helmet from "react-helmet";

import { Link } from "react-router-dom";

import { showAlertMessage, EmptyLabel } from "../../resusableComponents/common/formElements";
import { hasAccess } from "../../resusableComponents/hoc/AccessManagement";
import { withRouter } from "../../resusableComponents/hoc/withRouter";
import { updateLoadingState } from "../../redux/actions/application";
import { fetchCampaignDetails, initiateCampaignDeepResearchPdf, getCampaignDeepResearchPdfStatus, downloadCampaignDeepResearchPdf, saveDeepResearchHtmlContent } from "../../redux/services/campaign";
import { AppLoader } from "../../resusableComponents/loaders/AppLoader";
import { downloadExcelFile } from "../../helpers/utils";

class ViewDeepResearchReport extends Component {

  constructor(props) {
    super(props);

    this.state = {
      campaignId: _get(this.props, "params.id", 0),
      campaignDetails: {},
      isDownloadingPdf: false,
      isEditMode: false,
      isSavingReport: false,
      serverHtmlContent: "",
    };

    this.reportContainerRef = createRef();
    this._pollIntervalId = null;
  }

  componentDidMount() {
    this._fetchPageData();
  }

  componentWillUnmount() {
    if (this._pollIntervalId) {
      clearInterval(this._pollIntervalId);
      this._pollIntervalId = null;
    }
  }

  _fetchPageData = async () => {
    const { updateLoadingState } = this.props;

    if (typeof updateLoadingState === "function") { updateLoadingState(true); }

    try {
      await Promise.all([
        this._fetchCampaignDetails(),
      ]);
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  };

  _fetchCampaignDetails = async () => {
    const { campaignId } = this.state;

    if (!campaignId) { return; }

    try {
      const response = await fetchCampaignDetails(campaignId);

      if (_get(response, "flag", false) === true) {
        const campaignDetails = _get(response, "data.campaign_detail[0]", {});
        const serverHtmlContent = _get(campaignDetails, "html_content", "");
        this.setState({ campaignDetails, serverHtmlContent, isEditMode: false });
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while fetching campaign details."));
      }
    } catch (err) {
      showAlertMessage((err.message || "Something went wrong while fetching campaign details."));
    }
  };

  _handleContainerClick = (e) => {
    const btn = e.target.closest('.nav-link');
    if (!btn) return;

    const targetTabId = btn.getAttribute('data-target');
    if (!targetTabId) return;

    // Walk up to the parent <ul class="nav nav-tabs"> that owns this button
    const navList = btn.closest('ul.nav');
    if (!navList) return;

    // 1. Deactivate all sibling nav-link buttons within this nav group only
    navList.querySelectorAll('.nav-link').forEach(sibling => sibling.classList.remove('active'));

    // 2. Activate the clicked button
    btn.classList.add('active');

    // 3. Find the tab-content that is a sibling of this nav list
    //    (works for both outer tabs and nested inner tabs)
    const tabContent = navList.parentElement
      ? navList.parentElement.querySelector(':scope > .tab-content')
      : null;

    if (tabContent) {
      // Hide all panes inside this tab-content only
      tabContent.querySelectorAll(':scope > .tab-pane').forEach(pane => {
        pane.classList.remove('active');
      });

      // Show the target pane
      const targetPane = tabContent.querySelector(`#${targetTabId}`);
      if (targetPane) {
        targetPane.classList.add('active');
      }
    }
  };

  _handleDownloadPdf = async () => {
    const { intl } = this.props;
    const { campaignId } = this.state;

    this.setState({ isDownloadingPdf: true });

    try {
      // Step 1: Initiate PDF generation
      const initiateResponse = await initiateCampaignDeepResearchPdf(campaignId);

      if (_get(initiateResponse, "flag", false) !== true) {
        showAlertMessage(_get(initiateResponse, "message", intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." })));
        this.setState({ isDownloadingPdf: false });
        return;
      }

      const initiateData = _get(initiateResponse, "data", {});

      if (_get(initiateData, "ready", false) === true) {
        // Instant path: PDF already exists, go straight to download
        await this._downloadPdf(campaignId, null);
      } else {
        // Async path: need to poll status
        const requestId = _get(initiateData, "request_id", null);

        if (!requestId) {
          showAlertMessage(intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." }));
          this.setState({ isDownloadingPdf: false });
          return;
        }

        // Step 2: Poll status every 3 seconds
        this._pollIntervalId = setInterval(async () => {
          try {
            const statusResponse = await getCampaignDeepResearchPdfStatus(requestId);

            if (_get(statusResponse, "flag", false) !== true) {
              clearInterval(this._pollIntervalId);
              this._pollIntervalId = null;
              showAlertMessage(_get(statusResponse, "message", intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." })));
              this.setState({ isDownloadingPdf: false });
              return;
            }

            const statusData = _get(statusResponse, "data", {});
            const status = _get(statusData, "status", "");
            const ready = _get(statusData, "ready", false);

            if (status === "failed") {
              clearInterval(this._pollIntervalId);
              this._pollIntervalId = null;
              showAlertMessage(_get(statusData, "error", intl.formatMessage({ id: "view_deep_research_report.error.pdf_not_ready", defaultMessage: "PDF generation failed. Please try again." })));
              this.setState({ isDownloadingPdf: false });
              return;
            }

            if (ready === true || status === "completed") {
              clearInterval(this._pollIntervalId);
              this._pollIntervalId = null;
              // Step 3: Download the PDF
              await this._downloadPdf(campaignId, requestId);
            }
            // else: still pending/generating — keep polling
          } catch (pollErr) {
            clearInterval(this._pollIntervalId);
            this._pollIntervalId = null;
            showAlertMessage(pollErr.message || intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." }));
            this.setState({ isDownloadingPdf: false });
          }
        }, 3000);
      }
    } catch (err) {
      showAlertMessage(err.message || intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." }));
      this.setState({ isDownloadingPdf: false });
    }
  };

  _downloadPdf = async (campaignId, requestId) => {
    const { intl } = this.props;

    try {
      const downloadResponse = await downloadCampaignDeepResearchPdf(campaignId, requestId);

      if (_get(downloadResponse, "flag", false) === true) {
        const contentType = _get(downloadResponse, "data.contentType", "");
        const contents = _get(downloadResponse, "data.fileContents", "");
        const filename = _get(downloadResponse, "data.filename", "");

        if (contentType && contents && filename) {
          downloadExcelFile(contentType, contents, filename);
          showAlertMessage(intl.formatMessage({ id: "view_deep_research_report.success.download_pdf", defaultMessage: "PDF report downloaded successfully." }), "success");
        } else {
          showAlertMessage(intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." }));
        }
      } else {
        showAlertMessage(_get(downloadResponse, "message", intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." })));
      }
    } catch (err) {
      showAlertMessage(err.message || intl.formatMessage({ id: "view_deep_research_report.error.download_pdf", defaultMessage: "Something went wrong while downloading the PDF report." }));
    } finally {
      this.setState({ isDownloadingPdf: false });
    }
  };

  _handleEditReport = () => {
    this.setState({ isEditMode: true });
  };

  _handleCancelEdit = () => {
    const { serverHtmlContent } = this.state;
    // Restore last server state on the reportContainerRef
    if (this.reportContainerRef.current) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(serverHtmlContent, "text/html");
        const bodyContent = doc.body ? doc.body.innerHTML : serverHtmlContent;
        this.reportContainerRef.current.innerHTML = bodyContent;
      } catch (e) {
        this.reportContainerRef.current.innerHTML = serverHtmlContent;
      }
    }
    this.setState({ isEditMode: false });
  };

  _handleSaveReport = async () => {
    const { intl } = this.props;
    const { campaignId } = this.state;

    if (!this.reportContainerRef.current) { return; }

    // Normalise tab state before snapshotting HTML:
    // Reset every tab group so the first nav-link & first tab-pane are active,
    // regardless of which tab the user had open when they clicked Save.
    const container = this.reportContainerRef.current;

    // 1. Nav buttons — reset active class to first sibling in each nav list
    container.querySelectorAll("ul.nav").forEach((navList) => {
      const navLinks = navList.querySelectorAll(".nav-link");
      navLinks.forEach((link, idx) => {
        if (idx === 0) {
          link.classList.add("active");
        } else {
          link.classList.remove("active");
        }
      });
    });

    // 2. Tab panes — reset active class to first pane in each tab-content block
    container.querySelectorAll(".tab-content").forEach((tabContent) => {
      const panes = tabContent.querySelectorAll(":scope > .tab-pane");
      panes.forEach((pane, idx) => {
        if (idx === 0) {
          pane.classList.add("active");
        } else {
          pane.classList.remove("active");
        }
      });
    });

    // Read the normalised HTML as payload
    const editedHtml = container.innerHTML;

    this.setState({ isSavingReport: true });

    try {
      const response = await saveDeepResearchHtmlContent(campaignId, editedHtml);

      if (_get(response, "flag", false) === true) {
        showAlertMessage(
          _get(response, "message", intl.formatMessage({ id: "view_deep_research_report.success.save_report", defaultMessage: "Report saved successfully." })),
          "success"
        );
        // Reload from server to pick up the latest versioned content
        await this._fetchCampaignDetails();
      } else {
        const errMsg = _get(response, "message", "");
        showAlertMessage(errMsg || intl.formatMessage({ id: "view_deep_research_report.error.save_report", defaultMessage: "Something went wrong while saving the report." }));
      }
    } catch (err) {
      showAlertMessage(err.message || intl.formatMessage({ id: "view_deep_research_report.error.save_report", defaultMessage: "Something went wrong while saving the report." }));
    } finally {
      this.setState({ isSavingReport: false });
    }
  };

  _renderButtons = () => {
    const { campaignId, isDownloadingPdf, isEditMode, isSavingReport } = this.state;

    return (
      <div className="row gy-4 mb-4">
        {hasAccess("campaigns", "dashboard") && (
          <div className="col-lg-7 d-flex col-lg-auto">
            <div className="input-group">
              <span className="input-group-text bg-primary-66 border-primary-66 px-3 d-flex align-items-center">
                <img src={require("../../assets/icons/icon-dashboard-alt.svg").default} className="img-fluid" alt="img" />
              </span>
              <Link to={`/campaigns/dashboard/${campaignId}`} className="btn btn-primary waves-effect">
                <FormattedMessage id="campaign_content.btn.campaign_dashboard" defaultMessage="campaign dashboard" />
              </Link>
            </div>
          </div>
        )}
        <div className="col-lg-5 d-flex justify-content-end gap-2">
          <button
            className="btn btn-outline-primary waves-effect"
            onClick={() => this._handleDownloadPdf()}
            disabled={isDownloadingPdf || isEditMode}
          >
            <FormattedMessage id="view_deep_research_report.btn.download_pdf" defaultMessage="Download PDF" />
          </button>

          {hasAccess("campaigns", "dashboard", ["edit-deep-research-report"]) && !isEditMode && (
            <button
              className="btn btn-outline-secondary waves-effect"
              onClick={() => this._handleEditReport()}
              disabled={isDownloadingPdf}
            >
              <FormattedMessage id="view_deep_research_report.btn.edit_report" defaultMessage="Edit" />
            </button>
          )}

          {hasAccess("campaigns", "dashboard", ["view-deep-research-report"]) && isEditMode && (
            <>
              <button
                className="btn btn-secondary waves-effect"
                onClick={() => this._handleCancelEdit()}
                disabled={isSavingReport}
              >
                <FormattedMessage id="view_deep_research_report.btn.cancel_edit" defaultMessage="Cancel" />
              </button>
              <button
                className="btn btn-primary waves-effect"
                onClick={() => this._handleSaveReport()}
                disabled={isSavingReport}
              >
                {isSavingReport
                  ? <FormattedMessage id="view_deep_research_report.btn.saving_report" defaultMessage="Saving..." />
                  : <FormattedMessage id="view_deep_research_report.btn.save_report" defaultMessage="Save" />
                }
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  _renderReportContent = () => {
    const { campaignDetails, isEditMode, isSavingReport } = this.state;
    const reportHtmlContent = _get(campaignDetails, "html_content", "");

    if (!reportHtmlContent) {
      return (
        <div className="d-flex justify-content-center align-items-center py-6">
          <EmptyLabel />
        </div>
      );
    }

    let bodyContent = reportHtmlContent;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(reportHtmlContent, "text/html");
      if (doc.body) {
        // Remove contenteditable attribute from all elements to prevent unwanted editing when not in edit mode
        const editableElements = doc.body.querySelectorAll('[contenteditable]');
        editableElements.forEach(el => el.removeAttribute('contenteditable'));
        bodyContent = doc.body.innerHTML;
      }
    } catch (e) {
      console.error("Error parsing HTML", e);
    }

    return (
      <div className="card">
        {isEditMode && (
          <div className="card-header bg-warning-subtle border-warning d-flex align-items-center gap-2 px-4 py-2">
            <i className="ri-edit-line text-warning" />
            <span className="text-edit-warning fw-semibold">
              <FormattedMessage id="view_deep_research_report.edit_mode_notice" defaultMessage="You are in edit mode. Click on any text in the report below to edit it." />
            </span>
          </div>
        )}
        <div className="card-body p-4 deep-research-report-section">
          <div
            ref={this.reportContainerRef}
            onClick={(e) => this._handleContainerClick(e)}
            contentEditable={isEditMode}
            suppressContentEditableWarning={true}
            className={isEditMode ? `deep-research-report-section report-editable-container${isSavingReport ? " report-editable-container--saving" : ""}` : ""}
            dangerouslySetInnerHTML={{ __html: bodyContent }}
          />
        </div>
      </div>
    );
  };


  render() {
    const { intl } = this.props;
    const { campaignDetails, isDownloadingPdf } = this.state;

    const campaignName = _get(campaignDetails, "campaign_name", "");

    return (
      <>
        <Helmet>
          <title>
            {intl.formatMessage({ id: "funnel.view_deep_research_report", defaultMessage: "Analysis Report: Funnel Fuel" })}
          </title>
        </Helmet>

        {isDownloadingPdf && <AppLoader />}

        <div className="container-fluid flex-grow-1 px-6 py-6">

          {/* Campaign Header */}
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-5 py-3 px-4">
                    <p className="heading-04 text-uppercase">
                      {campaignName || <EmptyLabel />}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Page Title & Buttons */}
          <div className="row mt-5">
            <div className="col-lg-12">
              <h1 className="heading-04 fw-semibold text-dark-35 text-uppercase mb-5">
                <FormattedMessage id="view_deep_research_report.title" defaultMessage="Analysis Report" />
              </h1>
            </div>
          </div>

          {this._renderButtons()}

          {/* Report Content */}
          <div className="row mt-4">
            <div className="col-lg-12">
              {this._renderReportContent()}
            </div>
          </div>

        </div>
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  updateLoadingState: (data) => dispatch(updateLoadingState(data)),
});

export default withRouter(connect(null, mapDispatchToProps)(injectIntl(ViewDeepResearchReport)));
